import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Grid,
  Divider,
  Tabs,
  Tab,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import HistoryIcon from '@mui/icons-material/History'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import UploadHistoryRefreshButton from '../components/UploadHistoryRefreshButton'
import UploadHistoryTableWrapper from '../components/UploadHistoryTableWrapper'
import UploadImportStatusAlert from '../components/UploadImportStatusAlert'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'
import { getUploadSuccessMessage } from '../utils/uploadSuccessMessages'
import { formatDateTime } from '../utils/dateFormat'
import { productService } from '../services'
import { uploadLogService } from '../services/uploadLogService'

const BulkBarcodeUploadPage = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadResult, setUploadResult] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const [uploadLogs, setUploadLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({ open: false, uploadLog: null })

  const [exportingErrors, setExportingErrors] = useState(false)
  const [exportingDialogErrors, setExportingDialogErrors] = useState(false)
  const logsFetchInFlightRef = useRef(false)

  const downloadErrorsCsv = (rows, fileName = 'bulk-barcode-errors.csv') => {
    if (!Array.isArray(rows) || rows.length === 0) return

    const headers = [
      'row_number',
      'article_code',
      'product_name',
      'barcode',
      'reason',
      'error_type',
      'error_code',
    ]

    const normalize = (value) => {
      if (value === null || value === undefined) return ''
      return String(value)
    }

    const csvRows = rows.map((row) => [
      normalize(row.row_number),
      normalize(row.article_code || row.row_data?.article_code || row.row_data?.sku),
      normalize(row.product_name || row.row_data?.product_name),
      normalize(row.barcode || row.row_data?.barcode || row.row_data?.barcode_1),
      normalize(row.reason || row.error_message || 'Failed'),
      normalize(row.error_type),
      normalize(row.error_code),
    ])

    const csv = [
      headers.join(','),
      ...csvRows.map((cells) =>
        cells.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','),
      ),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) {
      return
    }
    logsFetchInFlightRef.current = true
    try {
      if (!silent) {
        setLogsLoading(true)
      }
      const response = await productService.getBulkBarcodeUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
      })
      const logsData = response.items || response.data || response.logs || []
      const totalCount =
        response.pagination?.total ||
        response.totalCount ||
        response.total ||
        (Array.isArray(logsData) ? logsData.length : 0)

      setUploadLogs(Array.isArray(logsData) ? logsData : [])
      setLogsTotalCount(totalCount)
    } catch (error) {
      console.error('Error fetching upload logs:', error)
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
        setMessage({
          type: 'error',
          text: error.response?.data?.message || 'Failed to fetch upload history',
        })
      }
    } finally {
      if (!silent) {
        setLogsLoading(false)
      }
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage])

  useEffect(() => {
    if (activeTab === 1) {
      fetchUploadLogs()
    }
  }, [activeTab, fetchUploadLogs])

  const handleViewUploadHistory = useCallback(() => {
    setActiveTab(1)
    fetchUploadLogs()
  }, [fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 1, fetchUploadLogs)

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: 'Please select a CSV file' })
      return
    }

    try {
      setLoading(true)
      setMessage(null)
      setUploadResult(null)

      const result = await productService.uploadBulkBarcodes(file)
      setUploadResult(result)
      setFile(null)

      setMessage({
        type: 'success',
        text: result?.message || getUploadSuccessMessage('bulkBarcode'),
        showHistoryLink: true,
      })
      fetchUploadLogs()
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to upload bulk barcode CSV',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadSample = async () => {
    try {
      const response = await productService.downloadBulkBarcodeSample()
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'bulk-barcodes-sample.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to download sample CSV',
      })
    }
  }

  const handleViewErrorDetails = async (logId) => {
    try {
      const response = await productService.getBulkBarcodeUploadLogDetails(logId)
      setErrorDetailsDialog({ open: true, uploadLog: response.data || response })
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch error details',
      })
    }
  }

  const handleExportImmediateErrors = () => {
    const failures = uploadResult?.failures || []
    if (!failures.length) return
    setExportingErrors(true)
    try {
      downloadErrorsCsv(
        failures,
        `bulk-barcode-errors-${new Date().toISOString().split('T')[0]}.csv`,
      )
    } finally {
      setExportingErrors(false)
    }
  }

  const handleExportDialogErrors = async () => {
    const logId = errorDetailsDialog.uploadLog?.id
    if (!logId) return
    setExportingDialogErrors(true)
    try {
      await uploadLogService.downloadErrorsCsvBlob(
        logId,
        `bulk-barcode-history-errors-${new Date().toISOString().split('T')[0]}.csv`,
      )
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to export errors CSV',
      })
    } finally {
      setExportingDialogErrors(false)
    }
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: '#1a1a1a' }}>
          Bulk Barcode Upload
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Upload barcode CSV and monitor processing history
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
            <Tab label="Upload" icon={<CloudUploadIcon />} iconPosition="start" />
            <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  {message && (
                    <UploadImportStatusAlert
                      severity={message.type}
                      onClose={() => setMessage(null)}
                      showHistoryLink={message.showHistoryLink}
                      onViewUploadHistory={handleViewUploadHistory}
                    >
                      {message.text}
                    </UploadImportStatusAlert>
                  )}
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Upload Barcode CSV
                  </Typography>
                  <Box
                    sx={{
                      border: isDragOver ? '2px solid #1B5E20' : '2px dashed #e90000',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragOver ? '#E8F5E9' : '#f8f9fb',
                      transition: 'all 0.3s ease',
                    }}
                    onClick={() => document.getElementById('bulk-barcode-file-input').click()}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragOver(true)
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      setIsDragOver(false)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDragOver(false)
                      const dropped = e.dataTransfer?.files?.[0]
                      if (!dropped) return
                      if (!dropped.name.toLowerCase().endsWith('.csv')) {
                        setMessage({ type: 'error', text: 'Please upload a CSV file only' })
                        return
                      }
                      setFile(dropped)
                    }}
                  >
                    <CloudUploadIcon sx={{ fontSize: 56, color: '#e90000', mb: 2 }} />
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                      {file ? file.name : isDragOver ? 'Drop CSV file here' : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      CSV file format
                    </Typography>
                    <input
                      id="bulk-barcode-file-input"
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={(e) => {
                        const selected = e.target.files?.[0]
                        if (!selected) return
                        if (!selected.name.toLowerCase().endsWith('.csv')) {
                          setMessage({ type: 'error', text: 'Please upload a CSV file only' })
                          return
                        }
                        setFile(selected)
                      }}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleUpload}
                    disabled={loading || !file}
                    sx={{ mt: 3, bgcolor: '#e90000', '&:hover': { bgcolor: '#b30000' } }}
                    startIcon={loading ? <CircularProgress color="inherit" size={20} /> : <CloudUploadIcon />}
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </Button>
                </CardContent>
              </Card>

              {uploadResult && (
                <Card sx={{ mt: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Upload Summary
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Processed: {uploadResult.processed || 0}, Appended: {uploadResult.appended || 0}, Skipped:{' '}
                      {uploadResult.skipped || 0}, Failed: {uploadResult.failures?.length || 0}
                    </Alert>

                    {!!uploadResult?.failures?.length && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={
                              exportingErrors ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
                            }
                            onClick={handleExportImmediateErrors}
                            disabled={exportingErrors}
                          >
                            {exportingErrors ? 'Exporting...' : 'Export Errors'}
                          </Button>
                        </Box>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                                <TableCell sx={{ fontWeight: 700 }}>Row</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Article Code</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Barcode</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(uploadResult.failures || []).map((failure, index) => (
                                <TableRow key={`${failure.row_number || 'row'}-${index}`}>
                                  <TableCell>{failure.row_number || '-'}</TableCell>
                                  <TableCell>{failure.article_code || '-'}</TableCell>
                                  <TableCell>{failure.product_name || '-'}</TableCell>
                                  <TableCell>{failure.barcode || '-'}</TableCell>
                                  <TableCell>{failure.reason || 'Failed'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Sample CSV
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                    Download sample CSV to match expected columns.
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadSample}
                    sx={{ color: '#e90000', borderColor: '#e90000' }}
                  >
                    Download Sample
                  </Button>
                </CardContent>
              </Card>

              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon sx={{ color: '#e90000' }} />
                    Notes
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    After upload, open Upload History to view the log entry, counts, and row-level error details.
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Error exports include row number, article code, product name, barcode, and reason.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Upload History
                </Typography>
                <UploadHistoryRefreshButton
                  onClick={fetchUploadLogs}
                  loading={logsLoading}
                />
              </Box>

              <UploadHistoryTableWrapper
                loading={logsLoading}
                isEmpty={uploadLogs.length === 0}
              >
                <>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                          <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Total</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Success</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Failed</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadLogs.map((log) => (
                          <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f8f9fb' } }}>
                            <TableCell>{log.file_name}</TableCell>
                            <TableCell>{formatDateTime(log.created_at)}</TableCell>
                            <TableCell align="center">{log.total_rows}</TableCell>
                            <TableCell align="center" sx={{ color: '#4caf50', fontWeight: 600 }}>
                              {log.successful_imports}
                            </TableCell>
                            <TableCell align="center" sx={{ color: '#f44336', fontWeight: 600 }}>
                              {log.failed_imports}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  bgcolor:
                                    log.status === 'completed'
                                      ? '#e8f5e9'
                                      : log.status === 'processing'
                                        ? '#fff3e0'
                                        : '#ffebee',
                                  color:
                                    log.status === 'completed'
                                      ? '#1b5e20'
                                      : log.status === 'processing'
                                        ? '#e65100'
                                        : '#c62828',
                                }}
                              >
                                {log.status}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {log.failed_imports > 0 ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<ErrorIcon />}
                                  onClick={() => handleViewErrorDetails(log.id)}
                                >
                                  View Errors
                                </Button>
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.75rem' }}>No errors</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={logsTotalCount}
                    page={logsPage}
                    onPageChange={(event, page) => setLogsPage(page)}
                    rowsPerPage={logsRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setLogsRowsPerPage(parseInt(event.target.value, 10))
                      setLogsPage(0)
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                  />
                </>
              </UploadHistoryTableWrapper>
            </CardContent>
          </Card>
        )}
      </Container>

      <UploadErrorDetailsDialog
        open={errorDetailsDialog.open}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
        uploadLog={errorDetailsDialog.uploadLog}
        onExportErrors={handleExportDialogErrors}
        exportingErrors={exportingDialogErrors}
      />
    </>
  )
}

export default BulkBarcodeUploadPage
